import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ludolio/shared-ui';
import { Plus, Gamepad2, Eye } from 'lucide-react';
import type { Game } from '@/types';

export const Route = createFileRoute('/_authenticated/games/')({
  component: GamesPage,
});

function GamesPage() {
  const user = useAuthStore((state) => state.user);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await apiClient.listDeveloperGames();
        if (response.success) {
          setGames(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch games:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  const getStatusBadge = (game: Game) => {
    if (game.reviewStatus === 'approved') {
      return <Badge variant="success">Published</Badge>;
    }
    if (game.reviewStatus === 'pending_review') {
      return <Badge variant="secondary">Pending Review</Badge>;
    }
    if (game.reviewStatus === 'rejected') {
      return <Badge variant="destructive">Rejected</Badge>;
    }
    return <Badge variant="outline">Draft</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">My Games</h1>
          <p className="text-muted-foreground mt-1">Manage your game submissions</p>
        </div>
        {user?.isVerifiedDeveloper && (
          <Link to="/games/add">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Game
            </Button>
          </Link>
        )}
      </div>

      {!user?.isVerifiedDeveloper && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="pt-6">
            <p className="text-yellow-600 dark:text-yellow-400">
              Your developer account is pending verification. You cannot submit games until an admin verifies your account.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Games</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : (
            <Table aria-label="All Games">
              <caption className="sr-only">All Games</caption>
              <TableHeader>
                <TableRow>
                  <TableHead>Game</TableHead>
                  <TableHead>App ID</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {games.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      No games yet.
                    </TableCell>
                  </TableRow>
                ) : games.map((game) => (
                  <TableRow key={game.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {game.iconUrl ? (
                          <img src={game.iconUrl} alt={game.title} className="w-10 h-10 rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                            <Gamepad2 className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <span className="font-medium">{game.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>{game.appId}</TableCell>
                    <TableCell>{game.version || '-'}</TableCell>
                    <TableCell>{getStatusBadge(game)}</TableCell>
                    <TableCell>
                      <Link to="/games/$gameId" params={{ gameId: game.id }}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

